import React from 'react';

import { Draggable } from 'react-beautiful-dnd';

import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler';

import { UserPicker } from '@qality/common-components';
import { ExecutionStatusLozenge } from '@qality/common-components/src/atoms/ExecutionStatusLozenge';
import { PremiumFeatureGuard } from '@qality/common-components/src/atoms/PremiumFeatureGuard';
import { Table } from '@qality/common-components/src/atoms/Table';
import { Text } from '@qality/common-components/src/atoms/Text';
import CheckboxWithTooltip from '@qality/common-components/src/molecules/CheckboxWithTooltip/CheckboxWithTooltip';
import { Issue } from '@qality/common-components/src/molecules/Issue';
import { NoPermissionToViewTestCase } from '@qality/common-components/src/molecules/NoPermissionToViewTestCase';
import {
  FEATURE_FLAG,
  UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE,
} from '@qality/common-config';
import { TestCaseInCycle } from '@qality/common-model';
import { UserOption } from '@qality/common-model/src/UserOption';
import { StorageOfUniqueServices } from '@qality/common-service';
import { EventUtils } from '@qality/common-utils';
import { DEFAULT_TEST_TYPE } from '@qality/common-utils/src/TestType.const';

import { IssueTestType } from '../../../../molecules/IssueTestType';
import BugsWithStatusList from '../../../BugsWithStatusList';
import { useTestCaseFilterContext } from '../../../TestCaseFilter/TestCaseFilter.provider';
import { useTestCycleSorterContext } from '../../../TestCycleSorter';
import TestCycleTcListTabItemMenu from '../../../TestCycleTcListTabItemMenu';
import {
  NoPermissionToViewTestCaseWrapper,
  TestCycleAlignRowStyled,
  TestCycleTestCasesListIssuesCellStyled,
  TestCycleUserPickerWrapper,
} from '../../../TestCycleTestCasesListTab.styled';
import { useTestCasesInCycleList } from '../../TestCaseInCycleListProvider';
import { TestCasesInCycleExecutionStatusSelect } from '../../TestCasesInCycleExecutionStatusSelect';
import {
  canEditExecutionAssignee,
  handleExecute,
  handleExecutionAssigneeChange,
} from './TestCaseInCycleTableRow.util';

type Props = {
  index: number;
  testCaseInCycle: TestCaseInCycle;
  isSelected: boolean;
  onTestCaseSelection: (testCase: TestCaseInCycle) => void;
  defaultUserPickerOptions: Promise<Array<UserOption>>;
};

export function TestCasesInCycleTableRow({
  testCaseInCycle,
  index,
  isSelected,
  onTestCaseSelection,
  defaultUserPickerOptions,
}: Props): JSX.Element {
  const { testCaseActions, testCycleActions, testCycle } =
    useTestCasesInCycleList();

  const { isTestCaseFilterActive } = useTestCaseFilterContext();
  const { isTestCycleSorted } = useTestCycleSorterContext();

  const hasLinkedIssues =
    testCaseInCycle && testCaseInCycle.issuesLinkedToExecution?.length > 0;

  const primaryAction = handleExecute.bind(this, testCaseInCycle);

  const executionAssigneeId =
    testCaseInCycle.testExecution?.executionAssignee?.getIdentifier();

  const selectedValue = executionAssigneeId
    ? {
        avatar: testCaseInCycle.testExecution?.executionAssignee.getAvatar(),
        label:
          testCaseInCycle.testExecution?.executionAssignee.getDisplayName(),
        value: testCaseInCycle.testExecution?.executionAssignee.getIdentifier(),
      }
    : undefined;

  const isDndDisabled =
    !FEATURE_FLAG.isPremium ||
    !testCycle.canEdit ||
    isTestCycleSorted ||
    isTestCaseFilterActive;

  const handleTestCaseSelection = () => {
    onTestCaseSelection(testCaseInCycle);
  };

  const isUserPickerVisible =
    defaultUserPickerOptions && testCaseInCycle.testCase;

  const isFirstRow = index === 0;

  return (
    <Draggable
      key={testCaseInCycle.id}
      draggableId={String(testCaseInCycle.id)}
      index={index}
      isDragDisabled={isDndDisabled}
    >
      {(provided, snapshot) => (
        <Table.DndTr
          key={testCaseInCycle.id}
          isClickable={testCaseInCycle.testCase}
          isHoverable
          onClick={primaryAction}
          {...provided.draggableProps}
          innerRef={provided.innerRef}
          isDragging={snapshot.isDragging}
        >
          <Table.DndTd {...provided.dragHandleProps}>
            <Table.ContentHelper
              onClick={EventUtils.stopPropagation}
              alignItems='center'
            >
              {!isDndDisabled && (
                <DragHandlerIcon label='Drag and drop handler icon' />
              )}
            </Table.ContentHelper>
          </Table.DndTd>

          <Table.CheckboxTd>
            <Table.ContentHelper
              justifyContent='center'
              alignItems='center'
              onClick={EventUtils.stopPropagation}
            >
              <CheckboxWithTooltip
                checkboxProps={{
                  onChange: handleTestCaseSelection,
                  isChecked: isSelected,
                  isDisabled: !FEATURE_FLAG.isPremium,
                }}
                tooltipProps={{
                  content: !FEATURE_FLAG.isPremium
                    ? 'Bulk actions are available in QAlity Plus.'
                    : '',
                }}
              />
            </Table.ContentHelper>
          </Table.CheckboxTd>

          <Table.Td>
            <Table.ContentHelper
              onClick={EventUtils.stopPropagation}
              alignItems='center'
            >
              <IssueTestType
                testType={
                  testCaseInCycle.testType
                    ? testCaseInCycle.testType
                    : DEFAULT_TEST_TYPE
                }
                isIconOnly
              />
            </Table.ContentHelper>
          </Table.Td>

          <Table.Td>
            <Table.ContentHelper
              onClick={EventUtils.stopPropagation}
              alignItems='center'
            >
              {testCaseInCycle.testCase ? (
                <Issue
                  issueKey={testCaseInCycle.testCase.key}
                  issueLink={StorageOfUniqueServices.get(
                    'URLService'
                  ).getIssueURL(testCaseInCycle.testCase.key)}
                  title={testCaseInCycle.testCase.fields.summary}
                />
              ) : (
                <NoPermissionToViewTestCaseWrapper>
                  <NoPermissionToViewTestCase />
                </NoPermissionToViewTestCaseWrapper>
              )}
            </Table.ContentHelper>
          </Table.Td>

          <Table.Td>
            <Table.ContentHelper onClick={EventUtils.stopPropagation}>
              <TestCycleAlignRowStyled>
                {testCaseInCycle.testCase && FEATURE_FLAG.isPremium ? (
                  <TestCasesInCycleExecutionStatusSelect
                    testCaseInCycle={testCaseInCycle}
                  />
                ) : (
                  <ExecutionStatusLozenge
                    status={testCaseInCycle.statusDetail}
                  />
                )}
              </TestCycleAlignRowStyled>
            </Table.ContentHelper>
          </Table.Td>

          <Table.Td>
            <Table.ContentHelper onClick={EventUtils.stopPropagation}>
              <TestCycleUserPickerWrapper>
                {isUserPickerVisible && (
                  <UserPicker
                    selectedValue={selectedValue}
                    defaultUserPickerOptions={defaultUserPickerOptions}
                    userId={testCaseInCycle.testExecution?.executionAssignee?.getIdentifier()}
                    onChange={(newExecutionAssignee) => {
                      handleExecutionAssigneeChange(
                        testCaseInCycle,
                        newExecutionAssignee,
                        testCycleActions.updateTestCycle,
                        testCycle
                      );
                    }}
                    isDisabled={
                      !canEditExecutionAssignee(
                        testCycle.canEdit,
                        testCaseInCycle
                      )
                    }
                    projectKey={testCycle.project.key}
                    tooltip={
                      FEATURE_FLAG.isPremium
                        ? undefined
                        : UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE
                    }
                  />
                )}
              </TestCycleUserPickerWrapper>
            </Table.ContentHelper>
          </Table.Td>

          <Table.Td>
            <Table.ContentHelper
              alignItems='center'
              onClick={EventUtils.stopPropagation}
            >
              <PremiumFeatureGuard
                free={
                  isFirstRow && (
                    <Text>Linked bugs available in QAlity Plus</Text>
                  )
                }
                premium={
                  <>
                    {hasLinkedIssues ? (
                      <TestCycleTestCasesListIssuesCellStyled>
                        {testCaseInCycle.issuesLinkedToExecution.map((bug) => {
                          return <BugsWithStatusList key={bug.id} bug={bug} />;
                        })}
                      </TestCycleTestCasesListIssuesCellStyled>
                    ) : (
                      <Text inactive>No issues found</Text>
                    )}
                  </>
                }
              />
            </Table.ContentHelper>
          </Table.Td>
          <Table.Td
            className='show-on-row-hover'
            onClick={EventUtils.stopPropagation}
          >
            {testCycle.canEdit && testCaseInCycle.testCase && (
              <Table.ContentHelper>
                <TestCycleTcListTabItemMenu
                  testCase={testCaseInCycle}
                  canEdit={testCycle.canEdit}
                  removeTestCase={testCaseActions.removeTestCase}
                  onExecute={primaryAction}
                />
              </Table.ContentHelper>
            )}
          </Table.Td>
        </Table.DndTr>
      )}
    </Draggable>
  );
}
